/**
 * Reconciliation tombstones (b19-fix PR V).
 *
 * Problem this solves: after a local dex wipe, `pullDexStateFromServer` merges
 * server rows back into the local store using last-writer-wins. If the server
 * has not been cleared yet (the deferred DELETE from PR U is still pending or
 * failed), OR a stale second device re-imports old rows later, the local wipe is
 * silently undone and the phantom shiny trades come back.
 *
 * PR U shipped an INTERIM gate that fully skipped the pull while the remote-wipe
 * marker was set. That only protected the window between the local wipe and the
 * first successful server DELETE; the moment the DELETE succeeded and cleared the
 * marker, nothing stopped a future stale row (re-import from a desync'd device, a
 * future legacy backfill run) from re-polluting the dex. This module is the
 * long-term replacement: a durable, per-device record of what the user removed,
 * checked on every pull.
 *
 * Two flavours of tombstone, both in one localStorage blob:
 *
 *   - PER-ENTRY: written when a `(variant, dexId)` entry transitions
 *     present -> missing locally (a user-initiated removal). Keyed by
 *     `${variant}:${dexId}` with the removal timestamp.
 *   - BULK: a single `bulkAt` timestamp written when the wipe migration runs.
 *     Any incoming server row whose `updatedAt` is older than `bulkAt` is
 *     dropped regardless of variant/dexId — one tombstone covers the whole wipe
 *     efficiently instead of one row per known dexId.
 *
 * Reconciliation rule (see `isRowTombstoned`): an incoming server row is dropped
 * iff a tombstone (per-entry or bulk) for it is NEWER than the row's updatedAt.
 * If the server row is newer, the tombstone is stale (the user re-added the
 * species on another device after removing it here) — the row is applied and the
 * per-entry tombstone is cleared by the caller.
 *
 * CROSS-DEVICE SEMANTICS (important): tombstones are PER-DEVICE and are NOT
 * synced. A wipe on THIS device empties THIS device's dex; OTHER devices keep
 * their data until they also run the migration. The deferred server DELETE in
 * PR U is the cross-device propagation mechanism: once any device fires the
 * DELETE, every device's next pull sees an empty server dex (so there is simply
 * nothing to re-import on the un-wiped devices either). This is the correct
 * "user wiped THIS device" semantics confirmed by the product owner.
 */

/** localStorage key holding the tombstone blob. */
export const TOMBSTONES_KEY = "nexus.dexTombstones.v1";

/**
 * Time-to-live for tombstones. A tombstone older than this whose target row
 * never arrived is no longer interesting and is garbage-collected on app start.
 * 90 days comfortably outlives any realistic propagation delay between devices.
 */
export const TOMBSTONE_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * On-disk shape of the tombstone blob.
 *
 *   - entries: per-entry tombstones keyed by `${variant}:${dexId}` -> epoch ms.
 *   - bulkAt:  timestamp of the most recent wipe, or null if none. Drops any
 *              server row older than this regardless of key.
 *   - bulkAckAt: set after a successful server DELETE clears the server rows.
 *              Marks the bulk tombstone "server-acknowledged" so future pulls
 *              know the server has already been emptied — newer rows (re-adds
 *              from another device) still apply normally.
 */
export interface TombstoneStore {
  entries: Record<string, number>;
  bulkAt: number | null;
  bulkAckAt: number | null;
}

function emptyStore(): TombstoneStore {
  return { entries: {}, bulkAt: null, bulkAckAt: null };
}

export function tombstoneKey(variant: string, dexId: number): string {
  return `${variant}:${dexId}`;
}

/** Read the tombstone blob. Total — never throws; returns an empty store on any error. */
export function readTombstones(): TombstoneStore {
  if (typeof localStorage === "undefined") return emptyStore();
  try {
    const raw = localStorage.getItem(TOMBSTONES_KEY);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw) as Partial<TombstoneStore> | null;
    if (!parsed || typeof parsed !== "object") return emptyStore();
    return {
      entries:
        parsed.entries && typeof parsed.entries === "object" ? parsed.entries : {},
      bulkAt: typeof parsed.bulkAt === "number" ? parsed.bulkAt : null,
      bulkAckAt: typeof parsed.bulkAckAt === "number" ? parsed.bulkAckAt : null,
    };
  } catch {
    return emptyStore();
  }
}

/** Persist the tombstone blob. Total — quota errors degrade to in-memory only. */
function writeTombstones(store: TombstoneStore): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(TOMBSTONES_KEY, JSON.stringify(store));
  } catch {
    /* quota — the wipe/removal still happened in the dex store; worst case a
       stale row could slip through on the next pull, which the user can clear */
  }
}

/**
 * Record a per-entry tombstone for an explicit `(variant, dexId)` removal.
 * `now` is injectable for tests; defaults to the wall clock.
 */
export function recordEntryTombstone(
  variant: string,
  dexId: number,
  now: number = Date.now(),
): void {
  const store = readTombstones();
  store.entries[tombstoneKey(variant, dexId)] = now;
  writeTombstones(store);
}

/**
 * Record the bulk wipe tombstone. Called by `runDexWipeMigration` on both the
 * fresh-wipe and audit-backfill paths so a previously migrated device still
 * protects itself against stale server rows. `now` is injectable for tests.
 */
export function recordBulkTombstone(now: number = Date.now()): void {
  const store = readTombstones();
  store.bulkAt = now;
  // A new wipe supersedes any prior server-ack: the server must be re-emptied
  // by PR U's deferred DELETE before the bulk tombstone is acknowledged again.
  store.bulkAckAt = null;
  writeTombstones(store);
}


/**
 * Clear the bulk wipe tombstone (both `bulkAt` and `bulkAckAt`). Used when the
 * clean-slate wipe is retired/defused: with the wipe gone, the bulk tombstone
 * would otherwise keep dropping legitimate server rows older than the wipe on
 * every pull — which would suppress a dex restored from the surviving local copy
 * (see dexResyncUp.ts). Per-entry tombstones (real user removals) are left
 * intact. Returns true if a bulk tombstone was present and cleared.
 */
export function clearBulkTombstone(): boolean {
  const store = readTombstones();
  if (store.bulkAt == null && store.bulkAckAt == null) return false;
  store.bulkAt = null;
  store.bulkAckAt = null;
  writeTombstones(store);
  return true;
}

/**
 * Decide whether an incoming server row is tombstoned (should be dropped).
 *
 * A row is dropped iff a tombstone NEWER than the row's `updatedAt` exists for
 * it — either the bulk wipe tombstone or a per-entry tombstone. A row strictly
 * newer than every applicable tombstone is a legitimate re-add (e.g. the user
 * re-caught the species on another device after the wipe) and is applied.
 */
export function isRowTombstoned(
  store: TombstoneStore,
  variant: string,
  dexId: number,
  updatedAt: number,
): boolean {
  if (store.bulkAt != null && updatedAt < store.bulkAt) return true;
  const entryAt = store.entries[tombstoneKey(variant, dexId)];
  if (entryAt != null && updatedAt < entryAt) return true;
  return false;
}

/**
 * Drop a per-entry tombstone once a newer server row has superseded it (the
 * user re-added the species elsewhere). The bulk tombstone is intentionally
 * NOT removed here — it is GC'd by TTL or superseded by a newer wipe. No-op if
 * absent. Returns true if a tombstone was removed.
 */
export function clearEntryTombstone(variant: string, dexId: number): boolean {
  const store = readTombstones();
  const key = tombstoneKey(variant, dexId);
  if (!(key in store.entries)) return false;
  delete store.entries[key];
  writeTombstones(store);
  return true;
}

/**
 * Garbage-collect tombstones older than `TOMBSTONE_TTL_MS`. Called once on app
 * start. A stale tombstone whose target row never arrived is no longer doing
 * any work, so dropping it keeps the blob bounded. Returns the number of
 * per-entry tombstones removed (the bulk tombstone is also expired if old).
 * `now` is injectable for tests.
 */
export function gcTombstones(now: number = Date.now()): number {
  const store = readTombstones();
  let removed = 0;
  let touched = false;
  for (const [key, ts] of Object.entries(store.entries)) {
    if (now - ts > TOMBSTONE_TTL_MS) {
      delete store.entries[key];
      removed += 1;
      touched = true;
    }
  }
  if (store.bulkAt != null && now - store.bulkAt > TOMBSTONE_TTL_MS) {
    store.bulkAt = null;
    store.bulkAckAt = null;
    touched = true;
  }
  if (touched) writeTombstones(store);
  return removed;
}

/** Clear all tombstones. Tests only. */
export function _resetTombstonesForTests(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(TOMBSTONES_KEY);
  } catch {
    /* ignore */
  }
}
